import { saveAs } from "file-saver"
import { utils, write } from "xlsx"

import type { UsageSummary, UsageWindow } from "./types"
import type { WorkSheet } from "xlsx"

function decorateSheet(ws: WorkSheet, rows: Record<string, unknown>[]): void {
  if (!ws["!ref"]) return
  const headers = Object.keys(rows[0] ?? {})

  ws["!cols"] = headers.map((header) => {
    const maxDataLen = rows.reduce((max, row) => {
      const v = row[header]
      return Math.max(max, v == null ? 0 : String(v).length)
    }, 0)
    return { wch: Math.min(Math.max(header.length, maxDataLen) + 2, 60) }
  })

  ws["!autofilter"] = { ref: ws["!ref"] }
}

function appendSheet(
  wb: ReturnType<typeof utils.book_new>,
  rows: Record<string, unknown>[],
  name: string
): void {
  const ws = utils.json_to_sheet(rows)
  decorateSheet(ws, rows)
  utils.book_append_sheet(wb, ws, name)
}

const SHEET_NAMES = {
  kpi: "KPIs",
  daily: "Daily activity",
  topSlides: "Top slides",
  topUsers: "Top users",
  topDecks: "Top decks",
  topSearches: "Top searches",
} as const

type SheetLabels = {
  columns: {
    metric: string
    value: string
    date: string
    uses: string
    exports: string
    slideId: string
    slideTitle: string
    deckId: string
    deckName: string
    userId: string
    userEmail: string
    events: string
    presentations: string
    query: string
    count: string
    avgResults: string
  }
  kpiRows: {
    activeUsers: string
    totalExports: string
    totalPresentations: string
    presentationSessions: string
    distinctSlidesUsed: string
  }
}

export function exportUsageSummaryToXlsx(
  data: UsageSummary,
  window: UsageWindow,
  labels: SheetLabels
): void {
  const wb = utils.book_new()

  const kpiRows = [
    {
      [labels.columns.metric]: labels.kpiRows.activeUsers,
      [labels.columns.value]: data.kpi.active_users,
    },
    {
      [labels.columns.metric]: labels.kpiRows.totalExports,
      [labels.columns.value]: data.kpi.total_exports,
    },
    {
      [labels.columns.metric]: labels.kpiRows.totalPresentations,
      [labels.columns.value]: data.kpi.total_presentations,
    },
    {
      [labels.columns.metric]: labels.kpiRows.presentationSessions,
      [labels.columns.value]: data.kpi.presentation_sessions,
    },
    {
      [labels.columns.metric]: labels.kpiRows.distinctSlidesUsed,
      [labels.columns.value]: data.kpi.distinct_slides_used,
    },
  ]
  appendSheet(wb, kpiRows, SHEET_NAMES.kpi)

  const dailyRows = data.daily.map((d) => ({
    [labels.columns.date]: d.date,
    [labels.columns.uses]: d.uses,
    [labels.columns.exports]: d.exports,
  }))
  appendSheet(wb, dailyRows, SHEET_NAMES.daily)

  const slideRows = data.top_slides.map((s) => ({
    [labels.columns.slideTitle]: s.slide_title ?? s.ai_title ?? "",
    [labels.columns.deckName]: s.deck_name ?? "",
    [labels.columns.uses]: s.uses,
    [labels.columns.slideId]: s.slide_id,
    [labels.columns.deckId]: s.deck_id ?? "",
  }))
  appendSheet(wb, slideRows, SHEET_NAMES.topSlides)

  const userRows = data.top_users.map((u) => ({
    [labels.columns.userEmail]: u.user_email ?? "",
    [labels.columns.events]: u.events,
    [labels.columns.exports]: u.exports,
    [labels.columns.presentations]: u.presentations,
    [labels.columns.userId]: u.user_id,
  }))
  appendSheet(wb, userRows, SHEET_NAMES.topUsers)

  const deckRows = data.top_decks.map((d) => ({
    [labels.columns.deckName]: d.deck_name ?? d.name ?? "",
    [labels.columns.uses]: d.uses,
    [labels.columns.deckId]: d.deck_id,
  }))
  appendSheet(wb, deckRows, SHEET_NAMES.topDecks)

  const searchRows = data.top_searches.map((s) => ({
    [labels.columns.query]: s.query,
    [labels.columns.count]: s.count,
    [labels.columns.avgResults]: s.avg_results,
  }))
  appendSheet(wb, searchRows, SHEET_NAMES.topSearches)

  const buffer = write(wb, { bookType: "xlsx", type: "array" })
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  })
  const stamp = new Date().toISOString().slice(0, 10)
  saveAs(blob, `slide-sense-usage-${window}-${stamp}.xlsx`)
}
